import { useState } from 'react';
import { TextField } from '~/components/form/TextField';
import { Dialog } from '~/components/ui/Dialog';
import { Button } from '../ui/buttons/Button';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onAssetTypeCreated: (assetType: string) => void;
};

export function CreateAssetTypeDialog({
  isOpen,
  onClose,
  onAssetTypeCreated,
}: Props) {
  const [assetType, setAssetType] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const handleCreate = async () => {
    if (!assetType.trim()) return;

    setIsCreating(true);
    try {
      // For asset types, we don't need an API call since they're just strings
      // The new type will be saved when the asset is created
      onAssetTypeCreated(assetType.trim());
      handleClose();
    } catch (error) {
      console.error('Failed to create asset type:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    setAssetType('');
    setIsCreating(false);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && assetType.trim()) {
      handleCreate();
    }
  };

  return (
    <Dialog
      show={isOpen}
      title='Create New Asset Type'
      onClose={handleClose}
      isLoading={isCreating}
    >
      <div className='p-5'>
        <div className='mb-4'>
          <TextField
            name='assetType'
            label='Asset Type'
            value={assetType}
            onChange={(e) => setAssetType((e.target as HTMLInputElement).value)}
            onKeyDown={handleKeyDown}
            placeholder='Enter asset type name...'
            required
            autoFocus
          />
        </div>

        <div className='flex justify-end gap-3'>
          <Button
            variant='secondary'
            onClick={handleClose}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button
            data-usetiful='create-asset-type'
            onClick={handleCreate}
            disabled={!assetType.trim() || isCreating}
            loading={isCreating}
          >
            Create Asset Type
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
